/**
 *
 * Organizations/Show/CampaignTemplates/DownloadHistory
 *
 */

import React from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format, parseISO } from 'date-fns'

// GraphQL
import { gql, useMutation } from '@apollo/client'

// Components
import Modal from 'components/Modal'
import QueryTable from 'components/Table/Query'

// Utils
import { ProgressCell } from 'components/Preferences/ReportsHistory'

const SEARCH_REPORTS = gql`
	query searchReports(
		$organization_id: ID
		$page: Int
		$page_size: Int
		$sort: String
		$sort_direction: String
		$filters: JSON
	) {
		search: search_reports(
			organization_id: $organization_id
			page: $page
			page_size: $page_size
			sort: $sort
			sort_direction: $sort_direction
			filters: $filters
		) {
			_total
			results: reports {
				id
				report_type
				status
				status_filterable
				arguments
				obj_exists
				progress
				created_at
				filename
			}
		}
	}
`

const DELETE_REPORT = gql`
	mutation deleteReport($id: ID) {
		deleteReport(input: { id: $id }) {
			report {
				id
			}
		}
	}
`

const TableContainer = styled.div`
	& > div {
		overflow-x: hidden;
		margin-bottom: 0;
	}

	& > div.float-right {
		margin-top: 20px;
	}

	.tbody {
		.td {
			padding-top: 0.9em;
			padding-bottom: 0.9em;
		}
	}
`

const ActionsCell = ({ report, deleteReport, loading }) => {
	const common_tooltip_props = {
		'data-for': 'context-tooltip',
		'data-place': 'bottom',
		'data-effect': 'float',
	}

	return (
		<div className="text-center">
			<button
				data-tip="Download Report"
				{...common_tooltip_props}
				className="btn btn-sm btn-unstyled mr-3"
				onClick={() => window.open(`${process.env.API_URL}/reports/${report.id}`, '_self')}
				disabled={!report.obj_exists || loading}
			>
				<FontAwesomeIcon icon="download" size="lg" className="text-success" />
			</button>
			<button
				data-tip="Remove Report"
				{...common_tooltip_props}
				className="btn btn-sm btn-unstyled"
				onClick={() => deleteReport({ variables: { id: report.id } })}
				disabled={loading}
			>
				<FontAwesomeIcon icon="trash" size="lg" className="text-danger" />
			</button>
		</div>
	)
}

function DownloadHistory({ organization_id, handleClose }) {
	const [deleteReport, { loading: deleting }] = useMutation(DELETE_REPORT)

	const columns = [
		{
			id: 'filename',
			Header: 'File Title',
			accessor: 'filename',
		},
		{
			id: 'status',
			Header: 'Status',
			accessor: ({ status }) => {
				const failed = status === 'Failed'
				const pending = status === 'Pending'
				const completed = status === 'Completed'

				let status_class

				if (failed) {
					status_class = 'danger'
				} else if (pending) {
					status_class = 'warning'
				} else if (completed) {
					status_class = 'success'
				} else {
					status_class = 'primary'
				}

				return (
					<div className="d-flex justify-content-center">
						<span className={`small py-1 px-2 rounded text-white bg-${status_class}`}>
							{completed || pending || failed ? status : 'Processing'}
						</span>
					</div>
				)
			},
			minWidth: 150,
			sortable: false,
		},
		{
			id: 'progress',
			Header: 'Progress',
			accessor: ({ progress }) => <ProgressCell progress={progress} />,
			minWidth: 200,
			sortable: false,
		},
		{
			id: 'created_at',
			Header: 'Creation Date',
			accessor: (r) => format(parseISO(r.created_at), 'MMM do, yyyy h:mma'),
			minWidth: 200,
		},
		{
			id: 'actions',
			accessor: (row) => <ActionsCell report={row} deleteReport={deleteReport} loading={deleting} />,
			sortable: false,
			minWidth: 120,
			withTooltip: true,
		},
	]
	return (
		<Modal show modalDidClose={handleClose} style={{ width: 1200 }}>
			{deleting && (
				<FontAwesomeIcon
					className="float-right text-primary mr-3 mt-2"
					icon="spinner"
					spin
					style={{ fontSize: '25px' }}
				/>
			)}

			<h1 className="mb-4">Campaign Template Download History</h1>

			<TableContainer className="border">
				<QueryTable
					query={SEARCH_REPORTS}
					variables={{ organization_id }}
					columns={columns}
					channels={[organization_id]}
					default_sort={{
						key: 'created_at',
						direction: 'desc',
					}}
					data_selector="search_reports"
					hide_page_size
				/>
			</TableContainer>
		</Modal>
	)
}

DownloadHistory.propTypes = {
	organization_id: PropTypes.string,
	handleClose: PropTypes.func,
}

export default DownloadHistory
